import React, { useState, useEffect } from 'react';
import { messageAPI } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import MessageThread from '../components/common/MessageThread';

const AttorneyMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [arrowHovered, setArrowHovered] = useState(null);
  const { t, language } = useLanguage();
  const dateLocale = language === 'uz' ? 'uz-UZ' : language === 'ru' ? 'ru-RU' : 'en-US';

  useEffect(() => {
    messageAPI.getInbox()
      .then(res => setMessages(res.data || []))
      .catch(() => setMessages([]))
      .finally(() => setLoading(false));
  }, []);

  const handleExpand = (id) => {
    const opening = expanded !== id;
    setExpanded(opening ? id : null);
    if (opening) {
      const msg = messages.find(m => m.id === id);
      if (msg && (!msg.read_by_attorney || msg.new_reply_for_attorney)) {
        messageAPI.markRead(id).catch(() => {});
        setMessages(prev => prev.map(m => m.id === id
          ? { ...m, read_by_attorney: true, new_reply_for_attorney: false }
          : m
        ));
      }
    }
  };

  const unread = messages.filter(m => !m.read_by_attorney).length;

  if (loading) return <div style={s.center}>{t('messages.loading')}</div>;

  return (
    <div style={s.page}>
      <div style={s.container}>
        <div style={s.header}>
          <h1 style={s.title}>{t('messages.inboxTitle')}</h1>
          {unread > 0 && <span style={s.badge}>{unread} {t('messages.newSuffix')}</span>}
        </div>

        {messages.length === 0 ? (
          <div style={s.empty}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '1rem' }}>
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
            <p style={{ color: '#718096' }}>{t('messages.noInboxMessages')}</p>
          </div>
        ) : (
          <div style={s.list}>
            {messages.map(msg => (
              <div
                key={msg.id}
                style={{ ...s.card, borderLeft: msg.read_by_attorney ? '3px solid #e2e8f0' : '3px solid #3182ce' }}
                onClick={() => handleExpand(msg.id)}
              >
                <div style={s.cardTop}>
                  <div style={s.senderInfo}>
                    {!msg.read_by_attorney && <span style={s.dot} />}
                    <span style={s.senderName}>{msg.sender_name || t('messages.anonymous')}</span>
                    {msg.sender_email && <span style={s.senderEmail}>{msg.sender_email}</span>}
                  </div>
                  <div style={s.cardRight}>
                    {msg.new_reply_for_attorney && (
                      <span style={s.replyBadge}>{t('messages.newReplyBadge')}</span>
                    )}
                    <div
                      onMouseEnter={() => setArrowHovered(msg.id)}
                      onMouseLeave={() => setArrowHovered(null)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '0.35rem',
                        padding: '0.2rem 0.45rem', borderRadius: '5px',
                        backgroundColor: arrowHovered === msg.id ? '#ebf8ff' : 'transparent',
                        transition: 'background-color 0.15s',
                      }}
                    >
                      <span style={{ ...s.date, color: arrowHovered === msg.id ? '#3182ce' : undefined }}>
                        {new Date(msg.created_at).toLocaleDateString(dateLocale)}
                      </span>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                        stroke={arrowHovered === msg.id ? '#3182ce' : '#a0aec0'}
                        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                        style={{ transform: expanded === msg.id ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', display: 'block' }}>
                        <path d="M6 9l6 6 6-6"/>
                      </svg>
                    </div>
                  </div>
                </div>

                <p style={{ ...s.preview, WebkitLineClamp: expanded === msg.id ? 'unset' : 2 }}>
                  {msg.situation}
                </p>

                {expanded === msg.id && msg.phone_preferred && (
                  <div style={s.phoneSection}>
                    <div style={s.phoneBadge}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '0.3rem' }}>
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.4 2 2 0 0 1 3.6 1.21h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 8.8a16 16 0 0 0 6 6l.86-.86a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                      </svg>
                      {t('messages.phonePreferredAttorney')}
                      {msg.phone_number && <strong style={{ marginLeft: '0.5rem' }}>{msg.phone_number}</strong>}
                      {msg.preferred_time && <span style={{ marginLeft: '0.5rem', color: '#2b6cb0' }}>· {msg.preferred_time}</span>}
                    </div>
                  </div>
                )}
                {expanded === msg.id && (
                  <div onClick={e => e.stopPropagation()}>
                    <MessageThread messageId={msg.id} currentUserType="attorney" />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const s = {
  page: { backgroundColor: '#f7fafc', minHeight: 'calc(100vh - 120px)', padding: '3rem 2rem' },
  container: { maxWidth: '800px', margin: '0 auto' },
  header: { display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem' },
  title: { fontSize: '1.8rem', fontWeight: '800', color: '#1a365d' },
  badge: { backgroundColor: '#3182ce', color: 'white', fontSize: '0.8rem', fontWeight: '700', padding: '0.25rem 0.65rem', borderRadius: '20px' },
  center: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh', color: '#718096' },
  empty: { textAlign: 'center', padding: '4rem 2rem', backgroundColor: 'white', borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  list: { display: 'flex', flexDirection: 'column', gap: '0.75rem' },
  card: { backgroundColor: 'white', borderRadius: '10px', padding: '1.25rem 1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', cursor: 'pointer', transition: 'box-shadow 0.15s' },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' },
  senderInfo: { display: 'flex', alignItems: 'center', gap: '0.5rem' },
  dot: { width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#3182ce', flexShrink: 0 },
  senderName: { fontWeight: '700', color: '#1a365d', fontSize: '0.95rem' },
  senderEmail: { color: '#a0aec0', fontSize: '0.82rem' },
  cardRight: { display: 'flex', alignItems: 'center', gap: '0.5rem' },
  date: { fontSize: '0.8rem', color: '#a0aec0' },
  preview: { color: '#4a5568', fontSize: '0.9rem', lineHeight: '1.6', display: '-webkit-box', WebkitBoxOrient: 'vertical', overflow: 'hidden' },
  phoneSection: { marginTop: '0.75rem' },
  phoneBadge: { display: 'inline-flex', alignItems: 'center', fontSize: '0.85rem', color: '#2b6cb0', backgroundColor: '#ebf8ff', padding: '0.35rem 0.85rem', borderRadius: '20px' },
  replyBadge: { fontSize: '0.72rem', fontWeight: '700', color: 'white', backgroundColor: '#e53e3e', padding: '0.15rem 0.55rem', borderRadius: '20px', whiteSpace: 'nowrap' },
};

export default AttorneyMessages;
